import React, { useEffect } from 'react';
import PageShell from './PageShell';
import Seo from '../components/Seo';
import { useI18n } from '../context/I18nContext';
import { publicApi } from '../api';
import { useApi } from '../hooks/useApi';
import { Spinner, EmptyState } from '../components/StateHelpers';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Image as ImageIcon, Newspaper, Smartphone } from 'lucide-react';
import { staticImg } from '../utils/media';
import GallerySection from '../components/GallerySection';

export function Blog() {
  const { lang, tr } = useI18n();
  const [selectedCat, setSelectedCat] = React.useState('all');
  const { data, loading } = useApi(() => publicApi.blog(lang), [lang]);
  useEffect(() => { document.title = 'Blog — Aegis Studios'; }, []);
  const items = data || [];

  // Extract unique categories
  const categories = React.useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      if (item.category_id && item.category_name) {
        map.set(item.category_id, item.category_name);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [items]);

  const filteredItems = React.useMemo(() => {
    if (selectedCat === 'all') return items;
    return items.filter((item) => String(item.category_id) === String(selectedCat));
  }, [items, selectedCat]);

  return (
    <PageShell eyebrow="BLOG & MAKALELER" title={<><span className="text-white">Aegis </span><span className="gold-text">Blog</span></>} subtitle={tr('blog.subtitle', 'Metin2 geliştirme, C++ & Python sistemleri, sunucu optimizasyonu ve geliştirici rehberleri.')}>
      <Seo title="Blog — Rehberler & Makaleler" description="Metin2 C++ & Python sistem entegrasyonu, sunucu optimizasyonu, geliştirici rehberleri ve güncellemeler." />
      <section className="pb-24">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 lg:px-10">
          {/* Category Filter Bar */}
          {categories.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-2 mb-10 pb-4 border-b border-white/5">
              <button
                onClick={() => setSelectedCat('all')}
                className={`h-9 px-5 rounded-full text-xs font-extrabold uppercase tracking-wider transition ${
                  selectedCat === 'all'
                    ? 'bg-[#e4b44e] text-black shadow-lg shadow-[#e4b44e]/20'
                    : 'bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                TÜM YAZILAR ({items.length})
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelectedCat(c.id)}
                  className={`h-9 px-5 rounded-full text-xs font-extrabold uppercase tracking-wider transition ${
                    String(selectedCat) === String(c.id)
                      ? 'bg-[#e4b44e] text-black shadow-lg shadow-[#e4b44e]/20'
                      : 'bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          )}

          {loading ? <Spinner /> : filteredItems.length === 0 ? (
            <EmptyState icon={Newspaper} title={tr('blog.coming', 'Yakında İlk Yazımız')} subtitle={tr('blog.coming_sub', 'Ekibimiz şu anda ilk makaleleri hazırlıyor.')} />
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {filteredItems.map((b) => (
                <article key={b.id} className="rounded-2xl border border-white/8 bg-gradient-to-b from-[#111015] to-[#0a090c] overflow-hidden card-hover flex flex-col group">
                  <Link to={`/blog/${b.slug}`} className="block aspect-video bg-[#0f0e12] overflow-hidden relative">
                    {b.image ? (
                      <img src={staticImg(b.image)} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1b1924] to-[#0a090c]">
                        <Newspaper className="w-12 h-12 text-white/20" />
                      </div>
                    )}
                    {b.category_name && (
                      <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-black/70 backdrop-blur border border-white/10 text-[#e4b44e]">
                        {b.category_name}
                      </span>
                    )}
                  </Link>
                  <div className="p-6 flex-1 flex flex-col justify-between">
                    <div>
                      {b.created_at && (
                        <div className="flex items-center gap-1.5 text-xs text-white/40 mb-3">
                          <Calendar className="w-3.5 h-3.5 text-[#e4b44e]" />
                          <span>{b.created_at.slice(0, 10)}</span>
                        </div>
                      )}
                      <h3 className="font-display text-lg font-bold text-white group-hover:text-[#e4b44e] transition-colors line-clamp-2 leading-snug">
                        <Link to={`/blog/${b.slug}`}>{b.title}</Link>
                      </h3>
                      <p className="text-white/60 text-xs mt-3 line-clamp-3 leading-relaxed">{b.excerpt}</p>
                    </div>
                    <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-between">
                      <Link to={`/blog/${b.slug}`} className="text-xs font-extrabold text-[#e4b44e] inline-flex items-center gap-1.5 group-hover:translate-x-1 transition-transform">
                        <span>Devamını Oku</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
}

export function Gallery() {
  const { tr } = useI18n();
  useEffect(() => { document.title = 'Galeri & Medya — Aegis Studios'; }, []);

  return (
    <PageShell eyebrow="GALERİ & MEDYA" title={<><span className="text-white">Medya </span><span className="gold-text">Galerisi</span></>} subtitle={tr('gallery.subtitle', 'Projelerimizden öne çıkan fotoğraf ve video önizlemeleri.')}>
      <Seo title="Galeri & Medya — Aegis Studios" description="Aegis Studios Metin2 server files, 3D modeller ve özel haritalar galeri ve videoları." />
      <GallerySection />
    </PageShell>
  );
}

export function MobileApps() {
  const { lang, tr } = useI18n();
  const { data, loading } = useApi(() => publicApi.mobileApps(lang), [lang]);
  useEffect(() => { document.title = 'Mobil Uygulamalar — Aegis Studios'; }, []);
  const items = data || [];

  return (
    <PageShell eyebrow="MOBİL UYGULAMALAR" title={<><span className="text-white">Yayınladığımız </span><span className="gold-text">Mobil Uygulamalar</span></>} subtitle={tr('mobile.subtitle', 'Aegis Studios tarafından geliştirilen ve mağazalarda yayınlanan resmi mobil uygulamalarımız.')}>
      <Seo title="Mobil Uygulamalar — Aegis Studios" description="Aegis Studios resmi mobil uygulamaları, iOS ve Android vitrini." />
      <section className="pb-24">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 lg:px-10">
          {loading ? <Spinner /> : items.length === 0 ? (
            <EmptyState icon={Smartphone} title="Henüz Eklenecek Uygulama Yok" subtitle="Yayınladığımız mobil uygulamalar çok yakında burada listelenecektir." />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {items.map((m) => (
                <div key={m.id} className="group relative rounded-3xl border border-white/10 bg-gradient-to-b from-[#14121b] to-[#0a090c] p-6 flex flex-col justify-between hover:border-[#e4b44e]/40 transition-all duration-300 hover:shadow-[0_0_30px_rgba(228,180,78,0.1)]">
                  <div>
                    <div className="flex items-center gap-4 mb-5">
                      <div className="w-16 h-16 rounded-2xl bg-[#0f0e15] border border-white/10 overflow-hidden flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform duration-300">
                        {m.image ? (
                          <img src={staticImg(m.image)} alt={m.title} className="w-full h-full object-cover" />
                        ) : (
                          <Smartphone className="w-8 h-8 text-[#e4b44e]" />
                        )}
                      </div>
                      <div>
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-extrabold uppercase tracking-wider mb-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> YAYINDA
                        </div>
                        <h3 className="font-display text-xl font-bold text-white group-hover:text-[#f4d896] transition-colors">{m.title}</h3>
                      </div>
                    </div>
                    {m.description && m.description.includes('<') ? (
                      <div 
                        className="prose prose-invert prose-p:text-white/70 prose-p:text-xs prose-p:my-1.5 prose-p:leading-relaxed prose-li:text-white/70 prose-li:text-xs prose-ul:my-1 max-w-none mb-6 line-clamp-6"
                        dangerouslySetInnerHTML={{ __html: m.description }}
                      />
                    ) : (
                      <p className="text-white/65 text-sm leading-relaxed mb-6 line-clamp-4">{m.description}</p>
                    )}
                  </div>

                  <div className="pt-4 border-t border-white/5 flex flex-wrap items-center gap-2">
                    {m.play_store_url ? (
                      <a href={m.play_store_url} target="_blank" rel="noreferrer" className="flex-1 h-10 px-4 rounded-xl bg-white/5 border border-white/10 hover:border-[#e4b44e]/50 hover:bg-[#e4b44e]/10 text-white text-xs font-bold inline-flex items-center justify-center gap-2 transition">
                        Google Play
                      </a>
                    ) : null}
                    {m.app_store_url ? (
                      <a href={m.app_store_url} target="_blank" rel="noreferrer" className="flex-1 h-10 px-4 rounded-xl bg-white/5 border border-white/10 hover:border-[#e4b44e]/50 hover:bg-[#e4b44e]/10 text-white text-xs font-bold inline-flex items-center justify-center gap-2 transition">
                        App Store
                      </a>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-16 text-center text-xs text-white/50 border-t border-white/5 pt-8">
            Mobil uygulamalarımızın gizlilik politikasına ulaşmak için <Link to="/pages/mobile-privacy" className="text-[#f4d896] hover:underline font-bold">Mobil Uygulama Gizlilik Politikası</Link> sayfamızı inceleyebilirsiniz.
          </div>
        </div>
      </section>
    </PageShell>
  );
}
